import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatINR, formatDate } from "../lib/api";
import { PageHeader, StatCard, SectionCard, Empty } from "../components/Shared";
import { Building2, Clock, FileText, IndianRupee, Search, Send, GitBranch } from "lucide-react";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function BuyerDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/buyer").then((r) => setData(r.data)).catch(() => setData({ active_vendors: 0, pending_onboardings: 0, open_rfqs: 0, total_spend: 0, spend_by_category: [], recent_activity: [] }));
  }, []);

  if (!data) {
    return (
      <div className="space-y-6">
        <PageHeader title="Buyer Dashboard" subtitle="At-a-glance health of your supplier ecosystem" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-28" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Buyer Dashboard"
        subtitle="At-a-glance health of your supplier ecosystem"
        actions={[
          <Button key="find" variant="outline" onClick={() => navigate("/discover")} data-testid="quick-find-vendors"><Search className="w-4 h-4 mr-1.5" /> Find Vendors</Button>,
          <Button key="rfq" className="bg-[#6366F1] hover:bg-[#4F46E5]" onClick={() => navigate("/rfq")} data-testid="quick-send-rfq"><Send className="w-4 h-4 mr-1.5" /> Send RFQ</Button>,
        ]}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-active-vendors" label="Active Vendors" value={data.active_vendors || 0} hint="Verified & onboarded" icon={Building2} />
        <StatCard testid="stat-pending-onb" label="Pending Onboardings" value={data.pending_onboardings || 0} hint="Awaiting approval" icon={Clock} accent="#F59E0B" />
        <StatCard testid="stat-open-rfqs" label="Open RFQs" value={data.open_rfqs || 0} hint="Awaiting responses" icon={FileText} accent="#3B82F6" />
        <StatCard testid="stat-total-spend" label="Total Spend" value={formatINR(data.total_spend || 0)} hint="All time" icon={IndianRupee} accent="#10B981" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
        <SectionCard testid="spend-by-category-card" title="Spend by Category" action={<Button size="sm" variant="ghost" onClick={() => navigate("/analytics")}>View analytics →</Button>}>
          <div className="h-72">
            {(data.spend_by_category || []).length === 0 ? (
              <Empty title="No spend data yet" subtitle="Purchase orders will appear here" />
            ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.spend_by_category || []} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                <XAxis dataKey="category" tick={{ fontSize: 11, fill: "#52525B" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#52525B" }} tickLine={false} axisLine={false} tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} />
                <Tooltip formatter={(v) => formatINR(v)} cursor={{ fill: "rgba(99,102,241,0.05)" }} />
                <Bar dataKey="amount" fill="#6366F1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            )}
          </div>
        </SectionCard>
        </div>

        <SectionCard testid="recent-activity-card" title="Recent Activity">
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {(data.recent_activity || []).length === 0 && (
              <Empty title="No activity yet" subtitle="Recent RFQ replies and applications will show here" />
            )}
            {(data.recent_activity || []).map((a, i) => (
              <div key={i} className="flex items-start gap-3 py-1.5">
                <div className="w-7 h-7 rounded-md bg-indigo-50 grid place-items-center text-[#6366F1] flex-shrink-0">
                  {a.type === "rfq_response" ? <FileText className="w-3.5 h-3.5" /> : <GitBranch className="w-3.5 h-3.5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-zinc-900 leading-snug">{a.title}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">{formatDate(a.time)}</div>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      <SectionCard testid="quick-actions-card" title="Quick actions">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <button onClick={() => navigate("/discover")} className="border border-zinc-200 hover:border-[#6366F1] rounded-md p-4 text-left transition-colors group">
            <Search className="w-5 h-5 text-[#6366F1] mb-2" />
            <div className="font-semibold text-sm">Find Vendors</div>
            <div className="text-xs text-zinc-500 mt-0.5">Browse 1,200+ verified suppliers</div>
          </button>
          <button onClick={() => navigate("/rfq")} className="border border-zinc-200 hover:border-[#6366F1] rounded-md p-4 text-left transition-colors group">
            <Send className="w-5 h-5 text-[#6366F1] mb-2" />
            <div className="font-semibold text-sm">Send RFQ</div>
            <div className="text-xs text-zinc-500 mt-0.5">Get competitive quotes</div>
          </button>
          <button onClick={() => navigate("/onboarding")} className="border border-zinc-200 hover:border-[#6366F1] rounded-md p-4 text-left transition-colors group">
            <GitBranch className="w-5 h-5 text-[#6366F1] mb-2" />
            <div className="font-semibold text-sm">Review Approvals</div>
            <div className="text-xs text-zinc-500 mt-0.5">{data.pending_onboardings || 0} pending</div>
          </button>
        </div>
      </SectionCard>
    </div>
  );
}
